import { Table } from '@mantine/core';
import { ICurrency } from '../../../../libraries/currencies';
import { ReadValues } from '../../../../libraries/transactions';
import { Language } from '../../../languages/languageHandler';
import { TransactionDenom } from '../transaction-data';
import { IQuantities } from '../../../utils/script';
import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import './styles.scss';

interface Props {
  currency: ICurrency;
  cash?: TransactionDenom[];
  values?: ReadValues[];
}

const prepareQuantitiesUnified = (cash: TransactionDenom[], currency: ICurrency): IQuantities[] => {
  if (!currency) return [];

  return currency.denominations.map(denom => {
    const matchedCash = cash.find(c => c.cid === currency.cid && c.cindex === denom.index);
    const received = matchedCash?.received ?? 0;
    const dispensed = matchedCash?.dispensed ?? 0;
    const qty = received + dispensed;
    const value = denom.value * currency.factor;
    const amount = Number((qty * value).toFixed(2));

    return {
      quantity: qty,
      value,
      amount,
      cindex: denom.index
    };
  });
};

const TransactionTable = ({ currency, cash = [] }: Props) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);

  const columns: IQuantities[] = prepareQuantitiesUnified(cash, currency);

  const nonZeroColumns = columns.filter(col => col.quantity > 0);
  const hasCash = nonZeroColumns.length > 0;

  return (
    <div>
      {/* --- Denominations Table --- */}
      {hasCash && (
        <Table.ScrollContainer minWidth={500}>
          <Table className="transaction-table" withColumnBorders withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th rowSpan={2}>{currency.currencyStr}</Table.Th>
                <Table.Th colSpan={currency.nCoins}>{lang.GLOBAL_COINS}</Table.Th>
                <Table.Th colSpan={currency.nBills}>{lang.GLOBAL_BILLS}</Table.Th>
              </Table.Tr>
              <Table.Tr>
                {currency.denominations.map(denom => (
                  <Table.Th key={denom.index}>{denom.value * currency.factor}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              <Table.Tr>
                <Table.Th>{lang.GLOBAL_QUANTITY}</Table.Th>
                {columns.map((col, index) => (
                  <Table.Td key={index}>{col.quantity}</Table.Td>
                ))}
              </Table.Tr>
              <Table.Tr>
                <Table.Th>{lang.GLOBAL_AMOUNT}</Table.Th>
                {columns.map((col, index) => (
                  <Table.Td key={index}>{col.amount.toFixed(2)}</Table.Td>
                ))}
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </div>
  );
};

export default TransactionTable;
